import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, first } from "rxjs/operators";
import { OnboardingService, OnboardingStepKey } from "src/app/services/onboarding.service";
import { combineLatest } from "rxjs";
import { LARGE_DIALOG_WINDOW_WIDTH } from "src/app/constants/technical.constants";
import { GroupAndMarketingConsentsComponent } from "src/app/modules/shared/components/group-and-marketing-consents/group-and-marketing-consents.component";
import { ConsentService } from "src/app/services/consent.service";
import { isBefore, subHours } from "date-fns";
import { CHECK_HOURS_AFTER_CONSENT_SUBMIT } from "src/app/constants/business.constants";
import { DialogService } from "src/app/services/dialog/dialog.service";

@Injectable({ providedIn: "root" })
export class NavigateToService {
  constructor(
    private readonly router: Router,
    private readonly dialogService: DialogService,
    private readonly onboardingService: OnboardingService,
    private readonly consentService: ConsentService,
  ) {}

  public navigateToUrlWithOnboarding(url: string): void {
    this.onboardingService
      .shouldOpen()
      .pipe(
        first(),
        catchError(async () => this.navigateWithOnboardingSteps(url)),
      )
      .subscribe((shouldOpen) => {
        if (shouldOpen) {
          this.navigateWithOnboardingSteps(url);
        } else {
          this.router.navigate([url], {
            queryParamsHandling: "merge",
          });
        }
      });
  }

  public navigateToUrlWithOnlyConsentOnboarding(url: string): void {
    combineLatest([
      this.onboardingService.isMissingDataStorageAndNorskPensjonConsents(),
      this.consentService.isMissingGroupAndMarketingConsents(),
      this.consentService.consents$,
    ])
      .pipe(first())
      .subscribe(([isMissingDataStorageAndNorskPensjonConsents, isMissingGroupAndMarketingConsents, consents]) => {
        if (isMissingDataStorageAndNorskPensjonConsents) {
          this.navigateWithOnboardingSteps(url, [OnboardingStepKey.NorskPensjonAndStorageConsent]);
        } else if (isMissingGroupAndMarketingConsents && this.hasPassedSomeTimeAfterConsentSubmit(consents)) {
          this.openGroupAndMarketingConsents(url);
        } else {
          this.router.navigate([url], {
            queryParamsHandling: "merge",
          });
        }
      });
  }

  public navigateWithExitUrl(route: string, exitUrl = this.router.url): Promise<boolean> {
    const exitUrlWithoutQueryParams = exitUrl.split("?")?.[0];

    return this.router.navigate([route], {
      queryParams: { exitUrl: exitUrlWithoutQueryParams },
      queryParamsHandling: "merge",
    });
  }

  /**
   * Check if there has passed some time after the user has submitted the first consent
   * This is needed to avoid for the user to see the second consent screen after the first one
   * @param consents
   * @private
   * @returns boolean
   */
  private hasPassedSomeTimeAfterConsentSubmit(consents: Consents.Consent[]): boolean {
    const thresholdDate = subHours(new Date(), CHECK_HOURS_AFTER_CONSENT_SUBMIT);

    return consents.some((item) => {
      const createdDate = new Date(item.createdTimestamp);
      return isBefore(createdDate, thresholdDate);
    });
  }

  private navigateWithOnboardingSteps(url: string, stepKeys?: OnboardingStepKey[]): void {
    this.onboardingService.open(
      {
        onComplete: () =>
          this.router.navigate([url], {
            queryParamsHandling: "merge",
          }),
      },
      stepKeys,
    );
  }

  private openGroupAndMarketingConsents(url: string): void {
    this.dialogService.open(GroupAndMarketingConsentsComponent, {
      width: LARGE_DIALOG_WINDOW_WIDTH,
      data: {
        url,
      },
    });
  }
}
